import Alert from 'common/components/Alert';
import * as logger from 'common/utils/Logger';

interface RTKQueryError {
    status: number;
    data: string;
    error: string;
}

export const defaultRetryOptions = {
    maxRetries: 5,
};

/**
 * Creates a RTK Query error object from a caught error
 * @param error - The caught error object from RTK Query
 * @returns RTK Query compatible error object
 */
export const createRTKError = (error: any): RTKQueryError => {
    // Handle RTK Query errors ({status, data})
    if (error?.status !== undefined) {
        return {
            status: error.status,
            data:
                typeof error.data === 'string'
                    ? error.data
                    : error.data?.message ||
                      JSON.stringify(error.data) ||
                      'Request failed',
            error: `Error ${error.status}: ${
                error.data?.message || error.data || 'Unknown error'
            }`,
        };
    }

    // Captured GET SuperAgent errors (fallback for non-RTK Query errors)
    if (error?.response) {
        const errorMessage =
            error.response.body?.error ||
            error.response.body?.message ||
            error.message;
        return {
            status: error.response.status,
            data:
                typeof errorMessage === 'string'
                    ? errorMessage
                    : JSON.stringify(errorMessage) || 'HTTP request failed',
            error: `Request failed: ${
                typeof errorMessage === 'string' ? errorMessage : error.message
            }`,
        };
    }

    // Handle generic errors, unhandled errors
    return {
        status: 500,
        data: error?.message || 'Network request failed',
        error: `${error?.toString() || 'Unknown error'}`,
    };
};

interface AlertOnErrorOptions {
    silent?: boolean;
}

/**
 * Creates an onQueryStarted handler that logs and optionally shows a toast from Alert when the query fails.
 * Use in RTK Query endpoint definitions to move error alerts out of components.
 *
 * @param message - Error message to log and display
 * @param options.silent - When true, only logs the error without showing a toast
 */
export const alertOnError =
    (message: string, { silent = false }: AlertOnErrorOptions = {}) =>
    async (
        _arg: unknown,
        { queryFulfilled }: { queryFulfilled: Promise<unknown> }
    ) => {
        try {
            await queryFulfilled;
        } catch (err) {
            const transformError = createRTKError(err);
            logger.error(message, transformError);
            if (!silent) {
                Alert.error(message);
            }
        }
    };
